import type { Option, RowKey } from "@tento-lona/sheets";
import type { SheetKey } from "../../client/sheet-key.ts";
import { TypedApi } from "@tento-core/typed-api";
import { NaiveDate } from "@tento-chrono";
import { SdkRequestError } from "../../errors.ts";
import { Network } from "../../network.ts";
import {
  RowNetworkKey,
  type SyncKey,
  type SyncPoll,
  SyncPollThrottledError,
  type SyncStatusResponse,
} from "@tento-lona/sheets";
import { requireNaiveDateRange, requireNonEmptyKey } from "../../sync/_contracts.ts";

export type { SyncStatusResponse };

type SyncRoutes = {
  "/api/sync/{syncKey}/status": {
    get: {
      config: {
        path: { syncKey: SyncKey };
      };
      response: Option<SyncStatusResponse>;
    };
  };
  "/api/debug/cells": {
    get: {
      config: {
        query: { row: string; start: NaiveDate; end: NaiveDate };
      };
      response: Network.SheetRowDataResponse;
    };
  };
  "/api/sheets/{sheetKey}/row/{rowKey}/sync-reset": {
    post: {
      config: {
        path: { sheetKey: string; rowKey: RowNetworkKey };
      };
      response: void;
    };
  };
};

const REVIVERS: Parameters<typeof TypedApi.makeWith<SyncRoutes>>[0] = {
  "/api/debug/cells": {
    parse: {
      get: Network.SheetRowDataResponse.parse,
    },
  },
};

/**
 * The client for the server's sync routes: polls a sync key's status and
 * triggers a row's sync.
 */
export class SyncClient {
  readonly #api: TypedApi<SyncRoutes>;

  constructor(
    accessCode: Option<string>,
    baseUrl: Option<string>,
    transport: Option<typeof fetch>,
  ) {
    this.#api = new TypedApi(REVIVERS, accessCode, baseUrl, null, null, transport, false);
  }

  /**
   * `pollStatus` as ONE stable `SyncPoll` function. `SyncKeyWatcher.for` keys
   * its client-wide watcher on the poll function's identity, so every poller
   * of this client (the rows accessor's sheets and the legacy
   * `RowsHttpBackend.applySync` path) must pass this same value to share one
   * loop per key, one pace, and one 429 pause.
   */
  readonly poll: SyncPoll = (syncKey, signal) => this.pollStatus(syncKey, signal);

  /**
   * `GET /api/sync/{syncKey}/status`. A non-200 REJECTS: a 429 (per-user
   * rate limit) as `SyncPollThrottledError`, so the poll coordinator pauses every
   * status poll instead of retrying on cadence; anything else as a plain
   * `Error`; a 200 with a null body resolves to `{ status: "complete" }`
   * (the server has no live job for this key). (`TypedApi.get` resolves a non-200 to `undefined`, which the
   * pollers only noticed as a `TypeError` on `.status` — a 429 looked like
   * any other transient failure and was retried at full rate.)
   *
   * `retryAfterMs` is `null`: `TypedApi`'s `ResponseError` does not carry
   * response headers, so the server's `Retry-After` is not visible here.
   *
   * `signal` cancels the request: once it aborts, the call rejects with the
   * platform's `AbortError`, and the `api-call` trace records the request as
   * aborted (`http.aborted`) rather than failed. `null` sends a poll nothing
   * can cancel.
   */
  async pollStatus(syncKey: SyncKey, signal: Option<AbortSignal>): Promise<SyncStatusResponse> {
    requireNonEmptyKey("SyncClient.pollStatus", "syncKey", syncKey);
    const config = { path: { syncKey } };
    const result = signal === null
      ? await this.#api.getResult("/api/sync/{syncKey}/status", config)
      : await this.#api.getResultAbortable("/api/sync/{syncKey}/status", signal, config);
    return result.match({
      ok: (response) => {
        // The server returns null for a missing or expired job. Settle it
        // through the shared watcher instead of retrying the full budget.
        if (response == null) return { status: "complete", error: null, pollUrl: null };
        return response;
      },
      err: (e) => {
        if (e.code === 429) throw new SyncPollThrottledError(null);
        throw new Error(`SyncClient.pollStatus: HTTP ${e.code} for ${syncKey}`);
      },
    });
  }

  /**
   * `GET /api/debug/cells`: sync `rowKey` over `[start, end]` server-side
   * and return the resulting cells. Throws `SdkRequestError` (boundary
   * `sync`, step `trigger-row-sync`) when the server refuses it or does not
   * answer.
   */
  triggerRowSync(
    rowKey: RowNetworkKey,
    start: NaiveDate,
    end: NaiveDate,
  ): Promise<Network.SheetRowDataResponse> {
    requireNonEmptyKey("SyncClient.triggerRowSync", "rowKey", rowKey);
    requireNaiveDateRange("SyncClient.triggerRowSync", "range", new NaiveDate.Range(start, end));
    return SdkRequestError.checkRequest(
      "sync",
      "trigger-row-sync",
      "GET",
      "/api/debug/cells",
      () =>
        this.#api.getResult("/api/debug/cells", {
          query: { row: rowKey, start, end },
        }),
    );
  }

  /**
   * `POST /api/sheets/{sheetKey}/row/{rowKey}/sync-reset`: drop the row's
   * sync state so the next read re-syncs it. Throws `SdkRequestError`
   * (boundary `sync`, step `reset`) when the server refuses it or does not
   * answer.
   */
  async resetSync(sheet: SheetKey, row: RowKey): Promise<void> {
    // The wire spelling: a persisted sheet by its id, an unpersisted one by its key.
    const sheetKey = sheet.sheetId?.toString() ?? sheet.toString();
    const rowKey = RowNetworkKey.of(row);
    requireNonEmptyKey("SyncClient.resetSync", "sheetKey", sheetKey);
    requireNonEmptyKey("SyncClient.resetSync", "rowKey", rowKey);
    await SdkRequestError.checkRequest(
      "sync",
      "reset",
      "POST",
      "/api/sheets/{sheetKey}/row/{rowKey}/sync-reset",
      () =>
        this.#api.postResult("/api/sheets/{sheetKey}/row/{rowKey}/sync-reset", {
          path: { sheetKey, rowKey },
        }),
    );
  }
}
